/**
 * LocalizedDateDisplay - Date display with user's preferred format
 */

import { parseISO } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useUserPreferences } from '../context';
import { LtrToken } from './common/LtrToken';
import { formatDateInZone, type DateStyle } from '../utils/dateFormat';

export type DateFormatOverride = 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd';

export interface LocalizedDateDisplayProps {
  date: Date | string | null | undefined;
  format?: DateStyle;
  /** Override the date format instead of using user preference */
  overrideDateFormat?: DateFormatOverride;
  /**
   * Set for a DATE-only value (a `DATE` column: a planning/start/end/due/target
   * date). Such a value carries no instant — it is parsed to local midnight and
   * written back from that same local frame — so it must be READ in that frame too.
   * Rendering it in the user's configured timezone shifts the calendar day by one
   * whenever that zone differs from the browser's, making the label disagree with
   * the value on disk.
   *
   * Leave unset for an INSTANT (a `TIMESTAMPTZ`: created_at, shipped_at, a meeting
   * time), which correctly renders in the user's configured zone.
   */
  dateOnly?: boolean;
  showTooltip?: boolean;
  emptyText?: string;
  className?: string;
}

export function LocalizedDateDisplay({
  date,
  format = 'medium',
  overrideDateFormat,
  dateOnly = false,
  showTooltip = false,
  emptyText = '-',
  className,
}: LocalizedDateDisplayProps) {
  const { formatDate, formatPlanningDate, formatOptions } = useUserPreferences();

  if (!date) {
    return <span className={className}>{emptyText}</span>;
  }

  const render = dateOnly ? formatPlanningDate : formatDate;

  const formattedDate = overrideDateFormat
    ? formatDateInZone(
        // A date-only string must be parsed to LOCAL midnight (`parseISO`) — bare
        // `new Date('2026-07-14')` reads it as UTC midnight, reintroducing the very
        // day-shift this mode exists to prevent. An instant parses either way.
        typeof date === 'string' ? (dateOnly ? parseISO(date) : new Date(date)) : date,
        format,
        overrideDateFormat,
        // Date-only is read in the ambient frame it was parsed in; an instant is read
        // in the user's configured zone.
        dateOnly ? undefined : formatOptions.timezone,
        // The override picks the day/month ORDER only; month and weekday NAMES still
        // belong to the viewer's language.
        formatOptions.locale,
      )
    : render(date, format);

  // For tooltip, show full format
  const tooltipDate = format !== 'full' ? render(date, 'full') : undefined;

  const content = <LtrToken className={className}>{formattedDate}</LtrToken>;

  if (showTooltip && tooltipDate && tooltipDate !== formattedDate) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltipDate}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
